import React from 'react';
import { UserProfile, Post } from '../../../app/domain/types/index.ts';
import { MessageSquare, Heart, Share2, MapPin, Users, Award, Shield } from 'lucide-react';

interface CommunityHubProps {
  user: UserProfile;
}

const CommunityHub: React.FC<CommunityHubProps> = ({ user }) => {
  
  const groups = [
    { name: `Type 2 Diabetes - ${user.location || 'Houston'}`, members: 1234, active: true },
    { name: "Post-Bariatric Support", members: 856, active: false },
    { name: "Meal Prep Enthusiasts", members: 5400, active: false }
  ];

  const posts: Post[] = [
    {
      id: '1',
      author: 'Sarah M.',
      avatar: 'SM',
      time: '2 hours ago',
      title: 'Success with cold rice trick!',
      content: 'I tried the tip about eating cold rice to lower glycemic impact. My post-meal reading was 120 instead of the usual 150! Has anyone else tried this with pasta?',
      likes: 24,
      comments: 8,
      tags: ['Diet', 'Success Story'],
      isHelpful: true
    },
    {
      id: '2',
      author: 'John D.',
      avatar: 'JD',
      time: '5 hours ago',
      title: 'Best affordable gyms in downtown?',
      content: 'Looking for a gym under $40/mo that has decent free weights. Most places I see are $80+.',
      likes: 12,
      comments: 15,
      tags: ['Question', 'Fitness']
    }
  ];

  return (
    <div className="p-6 md:p-8 h-full overflow-y-auto no-scrollbar pb-24">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">Community Hub</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Connect with people on the same journey in <span className="font-medium text-gray-900 dark:text-white">{user.location || 'Your Area'}</span></p>
        </div>
        
        <div className="flex gap-4">
            <div className="bg-white dark:bg-stone-800 px-4 py-2 rounded-xl shadow-sm border border-stone-100 dark:border-stone-700 flex items-center gap-3">
                <div className="bg-energy-100 dark:bg-energy-900/30 p-2 rounded-full text-energy-600 dark:text-energy-400">
                    <Award size={18} />
                </div>
                <div>
                    <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Your Rank</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{user.communityRank} Helper</p>
                </div>
            </div>
             <div className="bg-white dark:bg-stone-800 px-4 py-2 rounded-xl shadow-sm border border-stone-100 dark:border-stone-700 flex items-center gap-3">
                <div className="bg-health-100 dark:bg-health-900/30 p-2 rounded-full text-health-600 dark:text-health-400">
                    <Shield size={18} />
                </div>
                <div>
                    <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Help Points</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{user.communityPoints}</p>
                </div>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Sidebar: Groups */}
        <div className="lg:col-span-3 space-y-6">
            <div className="glass-panel p-5 rounded-2xl bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Users size={16} /> My Groups
                </h3>
                <div className="space-y-2">
                    {groups.map((group, idx) => (
                        <div key={idx} className={`p-3 rounded-xl cursor-pointer transition-colors ${group.active ? 'bg-health-50 dark:bg-health-900/30 border border-health-100 dark:border-health-800' : 'hover:bg-gray-50 dark:hover:bg-stone-800 border border-transparent'}`}>
                            <p className={`text-sm font-medium ${group.active ? 'text-health-700 dark:text-health-400' : 'text-gray-700 dark:text-gray-300'}`}>{group.name}</p>
                            <p className="text-xs text-gray-400 mt-1">{group.members} members</p>
                        </div>
                    ))}
                    <button className="w-full mt-2 py-2 text-xs font-semibold text-trust-600 dark:text-trust-400 border border-dashed border-trust-200 dark:border-trust-800 rounded-lg hover:bg-trust-50 dark:hover:bg-trust-900/20 transition-colors">
                        + Discover More
                    </button>
                </div>
            </div>

            <div className="glass-panel p-5 rounded-2xl bg-gradient-to-b from-energy-50/50 to-white dark:from-energy-900/10 dark:to-stone-800 border-stone-100 dark:border-stone-700">
                <h3 className="text-sm font-semibold text-energy-800 dark:text-energy-400 mb-3">Weekly Challenge</h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">"Break the Silence": Post one helpful tip this week to earn 50 points.</p>
                <div className="w-full bg-stone-200 dark:bg-stone-700 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-energy-500 h-full w-[0%]"></div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 text-right">0/1 completed</p>
            </div>
        </div>

        {/* Center: Feed */}
        <div className="lg:col-span-6 space-y-6">
            {/* Create Post Input */}
            <div className="glass-panel p-4 rounded-2xl flex gap-3 items-center bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-stone-700 flex items-center justify-center text-gray-500 dark:text-gray-300 font-bold">You</div>
                <input 
                    type="text" 
                    placeholder="Share your experience or ask a question..." 
                    className="flex-1 bg-gray-50 dark:bg-stone-800 border-none rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-health-500/20 outline-none transition-all text-gray-800 dark:text-white placeholder-gray-400"
                />
                <button className="p-2.5 bg-health-600 text-white rounded-full hover:bg-health-700 transition-colors shadow-md shadow-health-600/20">
                    <MessageSquare size={18} />
                </button>
            </div>

            {/* Posts */}
            {posts.map((post) => (
                <div key={post.id} className="glass-panel p-6 rounded-3xl animate-fade-in-up bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-trust-100 to-blue-200 dark:from-trust-900/50 dark:to-blue-900/50 text-trust-700 dark:text-trust-300 flex items-center justify-center font-bold text-sm shadow-inner">
                                {post.avatar}
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{post.author}</h4>
                                <p className="text-xs text-gray-400">{post.time}</p>
                            </div>
                        </div>
                        {post.isHelpful && (
                            <span className="px-2 py-1 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-bold uppercase tracking-wider rounded-md border border-green-100 dark:border-green-800 flex items-center gap-1">
                                <Award size={12} /> Helpful
                            </span>
                        )}
                    </div>
                    
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{post.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4">{post.content}</p>
                    
                    <div className="flex gap-2 mb-4">
                        {post.tags.map(tag => (
                            <span key={tag} className="text-[10px] bg-stone-100 dark:bg-stone-800 text-gray-500 dark:text-gray-400 px-2 py-1 rounded-full">{tag}</span>
                        ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-stone-800">
                        <div className="flex gap-6">
                            <button className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                                <Heart size={18} /> {post.likes}
                            </button>
                            <button className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-trust-600 dark:hover:text-trust-400 transition-colors">
                                <MessageSquare size={18} /> {post.comments}
                            </button>
                        </div>
                        <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>
            ))}
        </div>

        {/* Right Sidebar: Trending */}
        <div className="lg:col-span-3 space-y-6">
             <div className="glass-panel p-5 rounded-2xl bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">🔥 Hot Topics in {user.location || 'Houston'}</h3>
                <ul className="space-y-3">
                    <li className="text-sm text-gray-600 dark:text-gray-300 hover:text-health-600 dark:hover:text-health-400 cursor-pointer flex justify-between items-center group">
                        <span>#ColdRiceTrick</span>
                        <span className="text-xs bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded text-gray-400 group-hover:bg-health-50 dark:group-hover:bg-health-900/20 group-hover:text-health-600 dark:group-hover:text-health-400">124 posts</span>
                    </li>
                    <li className="text-sm text-gray-600 dark:text-gray-300 hover:text-health-600 dark:hover:text-health-400 cursor-pointer flex justify-between items-center group">
                        <span>#KetoRecipes</span>
                        <span className="text-xs bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded text-gray-400 group-hover:bg-health-50 dark:group-hover:bg-health-900/20 group-hover:text-health-600 dark:group-hover:text-health-400">89 posts</span>
                    </li>
                    <li className="text-sm text-gray-600 dark:text-gray-300 hover:text-health-600 dark:hover:text-health-400 cursor-pointer flex justify-between items-center group">
                        <span>#WalkingGroup</span>
                        <span className="text-xs bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded text-gray-400 group-hover:bg-health-50 dark:group-hover:bg-health-900/20 group-hover:text-health-600 dark:group-hover:text-health-400">56 posts</span>
                    </li>
                </ul>
            </div>

            <div className="glass-panel p-5 rounded-2xl bg-stone-900 dark:bg-stone-800 text-white relative overflow-hidden border-0">
                <div className="absolute top-0 right-0 w-32 h-32 bg-health-500/20 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <h3 className="text-sm font-semibold mb-2 relative z-10">Meetups Nearby</h3>
                <div className="space-y-3 relative z-10">
                    <div className="flex gap-3 items-start">
                        <div className="mt-1 bg-white/10 p-1.5 rounded-lg"><MapPin size={14} /></div>
                        <div>
                            <p className="text-sm font-medium">Sat 9:00 AM</p>
                            <p className="text-xs text-gray-400">Walking Group @ Memorial Park</p>
                        </div>
                    </div>
                </div>
                <button className="w-full mt-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition-colors">View All</button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default CommunityHub;